import { twMerge } from 'tailwind-merge';
import { getPalletTypeLabel } from '../../constants/pallets';
import { TransportQuote } from './types';
import {
  ShipmentMethodBucket,
  SHIPMENT_METHOD_BUCKETS,
  bucketForSource,
  formatCourierDistance,
  formatPrice,
  formatSourceLabel,
  formatVehicleLabel,
} from './shipmentMethods';

export interface QuoteOptionCardProps {
  quote: TransportQuote;
  isSelected: boolean;
  onSelect: () => void;
}

function primaryLineFor(
  quote: TransportQuote,
  bucket: ShipmentMethodBucket
): string {
  if (bucket === 'pallet-network') return quote.serviceName ?? '';
  if (bucket === 'courier') return formatVehicleLabel(quote.vehicleType);
  return formatSourceLabel(quote.source);
}

function secondaryLineFor(
  quote: TransportQuote,
  bucket: ShipmentMethodBucket
): string {
  if (bucket === 'pallet-network') {
    return quote.palletType ? `${getPalletTypeLabel(quote.palletType)} rate` : '';
  }
  if (bucket === 'courier') {
    return formatCourierDistance(quote.distanceMiles, quote.chargedMiles);
  }
  return (
    formatVehicleLabel(quote.vehicleType) ||
    getPalletTypeLabel(quote.palletType)
  );
}

function linesNamingTheSurchargesIncluded(quote: TransportQuote): string[] {
  const fuelSurcharge = quote.fuelSurchargeTotal
    ? `${formatPrice(quote.fuelSurchargeTotal, quote.currency)} fuel surcharge`
    : '';
  const selectedSurcharges = quote.selectedSurchargeTotal
    ? `${formatPrice(
        quote.selectedSurchargeTotal,
        quote.currency
      )} of surcharges`
    : '';
  if (fuelSurcharge && selectedSurcharges) {
    return [`includes ${fuelSurcharge}`, `and ${selectedSurcharges}`];
  }
  if (fuelSurcharge) return [`includes ${fuelSurcharge}`];
  if (selectedSurcharges) return [`includes ${selectedSurcharges}`];
  return [];
}

export function QuoteOptionCard({
  quote,
  isSelected,
  onSelect,
}: QuoteOptionCardProps) {
  const bucket = bucketForSource(quote.source);
  const typePillLabel =
    SHIPMENT_METHOD_BUCKETS.find((b) => b.id === bucket)?.title ?? bucket;
  const primaryLine = primaryLineFor(quote, bucket);
  const secondaryLine = secondaryLineFor(quote, bucket);
  const [firstSurchargeLine = '', secondSurchargeLine = ''] =
    linesNamingTheSurchargesIncluded(quote);

  return (
    <button
      type='button'
      onClick={onSelect}
      className={twMerge(
        'flex h-60 w-full flex-col items-center rounded-xl border-2 p-4 text-center transition-all',
        isSelected
          ? 'border-primary bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      )}
    >
      <span className='mb-2 inline-flex w-fit items-center whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600'>
        {typePillLabel}
      </span>
      <span
        className='mb-1 h-5 w-full truncate whitespace-nowrap text-sm font-medium text-gray-900'
        title={primaryLine || undefined}
      >
        {primaryLine}
      </span>
      <span
        className='mb-2 h-4 w-full truncate whitespace-nowrap text-xs text-gray-500'
        title={secondaryLine || undefined}
      >
        {secondaryLine}
      </span>
      <span className='mb-1 text-2xl font-bold text-gray-900'>
        {formatPrice(quote.price, quote.currency)}
      </span>
      <span
        className='h-4 w-full truncate whitespace-nowrap text-[10px] text-gray-500'
        title={firstSurchargeLine || undefined}
      >
        {firstSurchargeLine}
      </span>
      <span
        className='mb-3 h-4 w-full truncate whitespace-nowrap text-[10px] text-gray-500'
        title={secondSurchargeLine || undefined}
      >
        {secondSurchargeLine}
      </span>
      <div
        className={twMerge(
          'mt-auto w-full rounded-lg px-4 py-2 text-center text-sm font-medium transition-colors',
          isSelected
            ? 'bg-primary text-white'
            : 'border border-gray-300 bg-white text-gray-700'
        )}
      >
        {isSelected ? 'Selected' : 'Select'}
      </div>
    </button>
  );
}
